import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { Prompt } from 'react-router-dom';
import {
	Typography,
	List,
	ListItem,
	ListItemText,
	ListItemSecondaryAction,
	Box,
} from '@mui/material';

import { ProductTypes, ProductApi } from 'common/types';
import EmptyState from 'common/components/EmptyState';
import BackdropDialog from 'common/components/BackdropDialog';
import SaveButton from 'common/components/SaveButton';
import General from 'views/Product/EditProduct/General';
import Settings from 'views/Product/EditProduct/Settings';
import Pricing from './Pricing';
import VariantsTable from './VariantsTable';

import Container from 'components/Container';
import Banner from 'components/Banner';

import SetPageNavAction from 'components/Portals/SetPageNavAction';
import SetPageTitle, { ContextAction } from 'components/Portals/SetPageTitle';
import Embed from '../__components/Embed';

import { Routes, push, replace, isRouteActive } from 'routing';

import * as ActiveProductActions from 'actions/ActiveProductActions';
import * as ActiveProductSelectors from 'selectors/ActiveProductSelectors';
import * as NotificationActions from 'actions/NotificationActions';
import * as ShopSelectors from 'selectors/ShopSelectors';
import * as StockSelectors from 'selectors/StockSelectors';
import * as UserSelectors from 'selectors/UserSelectors';
import { cloneProduct } from 'views/Product/__utils/createProduct';
import * as IntercomService from 'services/intercom';
import { isKeycardProduct } from 'common/modules/skidata';
import { useTranslation } from 'services/localization/useTranslation';
import { RiLock2Line, RiArrowRightSLine } from 'react-icons/ri';
import Availability from './Availability';
import SkidataProductPricing from './Pricing/SkidataProductPricing';
import { getProductTags } from 'services/products';
import TagGroup from 'components/TagGroup';
import TabContainer from 'components/nav/TabContainer';
import { useHasFeature } from 'hooks/useHasFeature';

type ListenerRef = (() => void) | null;

const EditProduct = () => {
	const dispatch = useDispatch();
	const { t } = useTranslation();
	const { hasFeature } = useHasFeature();
	const { productId } = useParams<{ productId: string; tabId: string }>();
	const activeProduct = useSelector(ActiveProductSelectors.productDataWithChanges);
	const activeProductLoading = useSelector(ActiveProductSelectors.productLoading);
	const activeLocation = useSelector(UserSelectors.userActiveLocation);
	const activeLocationId = activeLocation?.id ?? null;
	const hasChanges = useSelector(ActiveProductSelectors.hasProductChanges);
	const isSaving = useSelector(ActiveProductSelectors.isSaving);
	const shop = useSelector(ShopSelectors.activeShopData);
	const shopDefaultLanguage = useSelector(ShopSelectors.activeShopDefaultLanguage);
	const maxProductOrderIndex = useSelector(StockSelectors.maxProductOrderIndex);
	const shopLocationsCount = useSelector(ShopSelectors.allShopLocationsCount);

	const isEditingDisabled = useSelector(ActiveProductSelectors.isEditingDisabled);
	const isSkidataProduct = useSelector(ActiveProductSelectors.isSkidataProduct);
	const isSkidataPackageProduct = useSelector(ActiveProductSelectors.isSkidataPackageProduct);

	const productListener = useRef<ListenerRef>(null);

	const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
	const [linkDialogOpen, setLinkDialogOpen] = useState<boolean>(false);
	const [duplicateDialogOpen, setDuplicateDialogOpen] = useState<boolean>(false);
	const [isDeleting, setIsDeleting] = useState<boolean>(false);
	const [isCloning, setIsCloning] = useState<boolean>(false);

	useEffect(() => {
		if (productId) {
			productListener.current = dispatch(
				ActiveProductActions.setProductListener(productId),
			) as ListenerRef;
		} else {
			replace(Routes.CATALOG_CREATE_PRODUCT);
		}
		return () => {
			productListener.current?.();
		};
	}, [dispatch, productId]);

	useEffect(() => {
		/** React might cleanup these refs before they can be called in our own cleanup below,
		 * so make sure we still have access to them */
		const removeProductListener = () => productListener.current?.();

		return () => {
			removeProductListener();
			dispatch(ActiveProductActions.reset());
		};
	}, [dispatch]);

	const handleSave = () => {
		dispatch(ActiveProductActions.saveChanges());
	};

	const handleDelete = async () => {
		setIsDeleting(true);
		dispatch(ActiveProductActions.deleteActiveProduct());
	};

	const createCloneProduct = useCallback(
		async (overrides?: Partial<ProductApi>) => {
			setIsCloning(true);
			try {
				if (!shop) {
					throw new Error(t('common:errors.error'));
				}
				const product = await cloneProduct({
					orderIndex: maxProductOrderIndex + 1,
					shop: {
						id: shop?.shopInfo.id,
						defaultLanguage: shopDefaultLanguage,
					},
					product: {
						...activeProduct!,
						...overrides,
					},
				});
				IntercomService.trackEvent(IntercomService.CREATED_PRODUCT);
				push(Routes.ProductPage(product.id));
				setDuplicateDialogOpen(false);
			} catch (e) {
				dispatch(
					NotificationActions.showNotification({
						variant: 'error',
						message: e.message,
					}),
				);
			}
			setIsCloning(false);
		},
		[activeProduct, maxProductOrderIndex, shop, shopDefaultLanguage, t, dispatch],
	);

	const handleDuplicate = useCallback(() => {
		if (shopLocationsCount > 1) {
			setDuplicateDialogOpen(true);
		} else {
			createCloneProduct();
		}
	}, [createCloneProduct, shopLocationsCount]);

	const renderSharedContent = () => {
		if (isEditingDisabled) {
			return (
				<Box sx={{ mb: 2 }}>
					<Banner
						variant="info"
						icon={RiLock2Line}
						title={t('access.editingDisabled', 'Editing is disabled')}
						description={t(
							'products.sharedProductEditDisabled',
							"This product is visible in some stores that you don't have access to. You can only edit inventory and visibility settings for the stores you have access to.",
						)}
					/>
				</Box>
			);
		}

		return null;
	};

	const contextActions: ContextAction[] = useMemo(() => {
		return [
			{
				label: t('product.duplicate', 'Duplicate'),
				onClick: handleDuplicate,
				disabled: isSkidataProduct,
			},
			{
				label: t('product.share', 'Share'),
				onClick: () => setLinkDialogOpen(true),
			},
			{
				label: t('common:actions.delete'),
				onClick: () => setDeleteDialogOpen(true),
				disabled: isEditingDisabled || isSkidataProduct,
			},
		];
	}, [handleDuplicate, isEditingDisabled, isSkidataProduct, t]);

	return (
		<>
			<SetPageNavAction
				priority={2}
				backAction={{ title: t('products.products'), to: Routes.CATALOG }}
			/>
			<EmptyState
				title={t('common:states.productNotFound', 'Product not found')}
				description={
					<span>
						{t('common:states.productWithIdNotFound', {
							productId: <strong>{productId}</strong>,
							defaultValue: `A product with the id {{productId}} doesn't seem to exist`,
						})}
					</span>
				}
				isLoading={activeProductLoading || isDeleting || isCloning}
				isEmpty={!activeProduct}
				callToActionText={t('product.backToProductsPage', 'Back to product list')}
				callToActionHandler={() => replace(Routes.CATALOG)}
				render={() => (
					<>
						<SetPageTitle
							priority={2}
							title={activeProduct?.name.def ?? ''}
							caption={<TagGroup tags={getProductTags(activeProduct, shopLocationsCount, t)} />}
							rightContent={
								<SaveButton disabled={!hasChanges} loading={isSaving} onClick={handleSave} t={t} />
							}
							contextActions={contextActions}
						/>
						<Prompt
							when={hasChanges}
							message={(location) => {
								if (!isRouteActive(Routes.CATALOG_PRODUCT_TAB, location.pathname)) {
									return t('common:prompts.unsavedChanges');
								}
								return true;
							}}
						/>
						<Container>
							{renderSharedContent()}
							<TabContainer
								basePath={Routes.ProductPage(productId)}
								tabs={[
									{
										path: 'info',
										label: t('product.basicInfo', 'Basic information'),
										component: General,
									},
									{
										path: 'pricing',
										label: t('product.pricing', 'Pricing'),
										component: isSkidataProduct ? SkidataProductPricing : Pricing,
										hidden: isSkidataPackageProduct,
									},
									{
										path: 'variants',
										label: t('product.variants', 'Variants'),
										component: VariantsTable,
										hidden: isSkidataProduct,
										locked: !hasFeature('VARIANTS', {
											scope: 'shop',
										}),
									},
									{
										path: 'availability',
										label: t('product.availability', 'Availability'),
										component: Availability,
										hidden: activeProduct?.type === ProductTypes.ACCESS,
									},
									{
										path: 'settings',
										label: t('product.settings', 'Settings'),
										component: Settings,
										disabled: !!(activeProduct && isKeycardProduct(activeProduct)),
									},
								]}
								defaultTab="info"
							/>
						</Container>
					</>
				)}
			/>
			<BackdropDialog
				isOpen={deleteDialogOpen}
				onClose={() => setDeleteDialogOpen(false)}
				label={t('product.deleteProductWarningTitle', 'Delete product?')}
				body={
					<Typography variant="body1">
						{t(
							'product.deleteProductWarningDescription',
							`You won't be able to recover this product if you delete it.`,
						)}
					</Typography>
				}
				variant="danger"
				confirm={{
					label: t('product.deleteProductConfirm', 'Yes, delete it'),
					callback: handleDelete,
					closeAfterDone: true,
				}}
				cancel={{
					label: t('product.deleteProductCancel', 'No, go back'),
				}}
			/>
			<BackdropDialog
				isOpen={duplicateDialogOpen}
				onClose={() => setDuplicateDialogOpen(false)}
				label={t('products.duplicateProduct', 'Duplicate product')}
				variant="primary"
				body={
					<List>
						<ListItem button disabled={isEditingDisabled} onClick={() => createCloneProduct()}>
							<ListItemText
								primary={t('products.exactCopy', 'Exact copy')}
								secondary={t(
									'products.exactCopyDescription',
									'Create an exact copy of this product and inventory',
								)}
							/>
							<ListItemSecondaryAction>
								<RiArrowRightSLine />
							</ListItemSecondaryAction>
						</ListItem>
						<ListItem
							button
							onClick={() => createCloneProduct({ visibleInLocations: [activeLocationId!] })}
						>
							<ListItemText
								primary={t('products.copyOnlyActiveStore', 'Only active store')}
								secondary={
									t(
										'products.copyOnlyActiveStoreDescription',
										'Create a copy that is visible only at your currently active store',
									) + ` (${activeLocation?.name})`
								}
							/>
							<ListItemSecondaryAction>
								<RiArrowRightSLine />
							</ListItemSecondaryAction>
						</ListItem>
					</List>
				}
				cancel={{
					label: t('common:actions.cancel'),
				}}
			/>
			<BackdropDialog
				isOpen={linkDialogOpen}
				onClose={() => setLinkDialogOpen(false)}
				label={t('product.shareProduct', 'Share product')}
				body={<Embed />}
				variant="primary"
				confirm={{
					label: t('common:actions.close'),
					callback: () => setLinkDialogOpen(false),
				}}
				width="md"
			/>
		</>
	);
};

export default EditProduct;
